import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import { IFilterSection } from "@models/IFilterConfig";
import SupplierService from "@services/SupplierService";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { buildVendorsFilterSections, VENDORS_DEFAULT_FILTERS } from "@config/filters/vendorsFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";
import { useVendorFilterData } from "./useVendorFilterData";

export const VENDORS_FILTER_ACTIONS_ID = "vendors-filter-actions";

/** Query params on the Vendors URL that are not filters (the legacy filter also wrote `order_dir`). */
const NON_FILTER_PARAMS = ["offset", "limit", "page", "order_by", "order_dir"];

type Props = {
    isSearching: boolean;
    /** Existing Vendors fetch: receives the complete `?a=b&c=d` query string */
    searchFn: (queryString: string) => void;
};

/**
 * Vendors (Customers) integration of the shared FilterBar (v2 search UI).
 * Screen key is ScreenOptionsEnum.ACCOUNTS ("accounts"), which is also the presets JSON key.
 * Renders once the option lists have loaded (see useVendorFilterData), then mounts the filter bar.
 */
export const VendorsFilterBar = (props: Props) => {
    const { lists, lookups } = useVendorFilterData();
    const { supplierModel } = useAppContextNonNull();
    const intl = useIntl();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () =>
            lists
                ? buildVendorsFilterSections({
                      supplierModel,
                      categories: lists.categories,
                      catalogs: lists.catalogs,
                      users: lists.users,
                      lookups,
                      formatMessage,
                  })
                : null,
        [lists, supplierModel, lookups, formatMessage]
    );

    if (!sections) {
        return null;
    }

    return <VendorsFilterBarLoaded sections={sections} {...props} />;
};

/** The filter bar proper: mounted with the final sections so deep links and the default preset see every field. */
const VendorsFilterBarLoaded = (props: Props & { sections: IFilterSection[] }) => {
    const { sections, searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const navigate = useNavigate();

    // Deep links (e.g. /vendors?name=acme&filter_inactive=false): read on mount and again on Back / Forward.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, NON_FILTER_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.ACCOUNTS);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            const parts = toQueryStringParts(query);
            const search = parts.length ? `?${parts.join("&")}` : "";

            // Keep the URL in sync so filtered views can be shared / deep-linked (as the legacy filter does).
            // The mount search replaces (no extra history entry); user searches push.
            navigate(
                { pathname: SupplierService.updateStringWithVendorOverride("/vendors", supplierModel), search },
                { replace: isFirstSearch.current }
            );
            isFirstSearch.current = false;

            searchFn(search);
        },
        [navigate, supplierModel, searchFn, isFirstSearch]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    // Wait for the first merge so a default preset can auto-load on mount.
    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.ACCOUNTS}
            mode={"drawer"}
            defaultFilters={VENDORS_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_accounts"}
            actionsContainerId={VENDORS_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default VendorsFilterBar;
